/* Pure helpers for the Audit Logs page: the date-range rules, the URL <-> filter
   mapping and the filter -> API query mapping. Kept free of React so they can
   be unit-tested on their own (see auditLogsUtils.test.js).

   API contract (backend card Vgb6pqVH, GET /audit-logs):
     from, to (ISO), userIds[], roles[], actions[], modules[], status, q,
     page, limit (25 | 50 | 100). Missing from/to = today. Any past `from` is
     accepted; `to` in the future or a span over 366 days -> 422. */

export const PAGE_SIZES = [25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;

// Same enumerations as the backend's GET /audit-logs/meta. Used as the
// fallback when /meta is unavailable so the filters still work.
export const AUDIT_ACTIONS = [
  "create",
  "update",
  "delete",
  "publish",
  "unpublish",
  "approve",
  "reject",
  "upload",
  "import",
  "export",
  "settings_change",
  "role_change",
  "login",
  "logout",
  "login_failed",
];

export const AUDIT_MODULES = [
  "content",
  "series",
  "media",
  "submissions",
  "bulk_upload",
  "taxonomy",
  "experience_builder",
  "users",
  "actors",
  "user_management",
  "roles",
  "ads",
  "subscriptions",
  "creators",
  "payouts",
  "suspected_activity",
  "monetization",
  "settings",
  "documents",
  "support",
  "auth",
  "creator_portal",
  "audit_logs",
];

export const STATUSES = ["success", "failed"];

export const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 days" },
  { key: "last30", label: "Last 30 days" },
  { key: "thisMonth", label: "This month" },
  { key: "ytd", label: "Year to date" },
  { key: "custom", label: "Custom" },
];

/** "settings_change" -> "Settings change", "bulk_upload" -> "Bulk upload". */
export const humanize = (key = "") => {
  const s = String(key).replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

/** 1 Jan of `now`'s year, 00:00 local. */
export const yearStart = (now = new Date()) => new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);

/** Keep a date from running past `now`. Any past date is allowed. */
const clamp = (d, now) => (d > now ? new Date(now.getTime()) : d);

/** { from, to } Dates for a preset, never in the future. */
export const presetRange = (preset, now = new Date()) => {
  const today = startOfDay(now);
  let from;
  let to = now;
  switch (preset) {
    case "yesterday": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      from = y;
      to = endOfDay(y);
      break;
    }
    case "last7": {
      from = new Date(today);
      from.setDate(from.getDate() - 6);
      break;
    }
    case "last30": {
      from = new Date(today);
      from.setDate(from.getDate() - 29);
      break;
    }
    case "thisMonth":
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case "ytd":
      from = yearStart(now);
      break;
    case "today":
    default:
      from = today;
      break;
  }
  return { from: clamp(from, now), to: clamp(to, now) };
};

/**
 * Validate a from/to pair against the API rules. Returns a message or null.
 * Mirrors the backend 422 so the user gets the error before a round-trip.
 */
export const validateRange = (from, to, now = new Date()) => {
  if (!(from instanceof Date) || isNaN(from)) return "Enter a valid start date and time";
  if (!(to instanceof Date) || isNaN(to)) return "Enter a valid end date and time";
  // 60s slack: the "now" the picker was opened at is a little behind real now.
  if (to.getTime() > now.getTime() + 60 * 1000) return "End can't be in the future";
  if (from > to) return "Start must be before end";
  if (to - from > 366 * 24 * 60 * 60 * 1000) return "Range can't be longer than 366 days";
  return null;
};

const pad = (n) => String(n).padStart(2, "0");

/** Date -> "YYYY-MM-DDTHH:mm" for <input type="datetime-local"> (local time). */
export const toLocalInputValue = (d) =>
  d instanceof Date && !isNaN(d)
    ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    : "";

/** "YYYY-MM-DDTHH:mm" (local) -> Date (or null). */
export const fromLocalInputValue = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * ISO -> "05 Apr 2026, hh:mm:ss AM" in the viewer's local time. The month is
 * spelled out so the date can't be misread whatever order the pickers use.
 * `seconds: false` drops the seconds (filter chip).
 */
export const formatTimestamp = (iso, { seconds = true } = {}) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const h = d.getHours();
  const time = `${pad(h % 12 || 12)}:${pad(d.getMinutes())}${seconds ? `:${pad(d.getSeconds())}` : ""}`;
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${time} ${h < 12 ? "AM" : "PM"}`;
};

// An ISO date-time as Prisma serialises it: "2026-10-29T08:00:48.514Z".
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

/** Changed-field values: ISO date-times read like the rest of the page. */
export const formatChangeValue = (v) =>
  typeof v === "string" && ISO_DATE_TIME.test(v) ? formatTimestamp(v) || v : v;

/** "last_published_at" / "dropShadow" / "userId" -> "Last published at" / "Drop shadow" / "User ID". */
export const fieldLabel = (key) =>
  humanize(
    String(key)
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .toLowerCase()
  ).replace(/\b(id|url|ip)\b/gi, (m) => m.toUpperCase());

/** One stored value as plain text: Yes/No, readable dates, "—" for empty. */
export const formatScalar = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(formatChangeValue(v));
};

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const isScalar = (v) => v === null || typeof v !== "object";

const deepEqual = (x, y) => {
  if (x === y) return true;
  if (Array.isArray(x) || Array.isArray(y)) {
    return Array.isArray(x) && Array.isArray(y) && x.length === y.length && x.every((v, i) => deepEqual(v, y[i]));
  }
  if (!isPlainObject(x) || !isPlainObject(y)) return false;
  const kx = Object.keys(x);
  return kx.length === Object.keys(y).length && kx.every((k) => deepEqual(x[k], y[k]));
};

/** What to call one entry of a list: its name, else its id, else its position. */
export const itemLabel = (item, index = 0) => {
  if (!isPlainObject(item)) return formatScalar(item);
  const name = item.name || item.title || item.label || item.slug;
  if (name) return String(name);
  return item.id !== undefined && item.id !== null ? `#${item.id}` : `Item ${index + 1}`;
};

const unionKeys = (a, b) => [...new Set([...Object.keys(a || {}), ...Object.keys(b || {})])];

/**
 * Before/after of one changed field -> one row per setting that actually
 * changed, so a nested settings object reads as
 *   "Published categories › Hero Banner › Card design › Drop shadow: Yes -> No"
 * instead of two near-identical JSON dumps. Lists of records are matched by
 * id (when ids are unique), else unchanged entries pair up wherever they moved
 * and the rest match by position, so a reorder, an insert or an edit to one
 * entry doesn't flag the whole list. Entries sharing a name get their id added
 * ("Trending (#70)").
 * Returns [{ label, before, after, entry }]; before/after may still be objects
 * when a whole list entry was added or removed (`entry` is then true).
 */
export const flattenChanges = (field, before, after) => {
  const rows = [];
  const walk = (b, a, path, entry = false) => {
    if (deepEqual(b, a)) return;
    if (isPlainObject(b) && isPlainObject(a)) {
      unionKeys(b, a).forEach((k) => walk(b[k], a[k], [...path, fieldLabel(k)]));
      return;
    }
    if (Array.isArray(b) && Array.isArray(a) && !(b.every(isScalar) && a.every(isScalar))) {
      const hasId = (x) => isPlainObject(x) && x.id !== undefined && x.id !== null;
      const uniqueIds = (list) => new Set(list.map((x) => String(x.id))).size === list.length;
      const byId = [...b, ...a].every(hasId) && uniqueIds(b) && uniqueIds(a);
      let pairs;
      if (byId) {
        const bMap = new Map(b.map((x) => [String(x.id), x]));
        const aMap = new Map(a.map((x) => [String(x.id), x]));
        pairs = [...new Set([...aMap.keys(), ...bMap.keys()])].map((k) => [bMap.get(k), aMap.get(k)]);
      } else {
        const bLeft = [...b];
        const aLeft = [];
        a.forEach((x) => {
          const j = bLeft.findIndex((y) => deepEqual(x, y));
          if (j >= 0) bLeft.splice(j, 1);
          else aLeft.push(x);
        });
        pairs = Array.from({ length: Math.max(aLeft.length, bLeft.length) }, (_, i) => [bLeft[i], aLeft[i]]);
      }
      const labels = pairs.map(([x, y], i) => itemLabel(y ?? x, i));
      const start = rows.length;
      pairs.forEach(([x, y], i) => {
        const item = y ?? x;
        const shared = labels.filter((l) => l === labels[i]).length > 1;
        const label = shared && hasId(item) ? `${labels[i]} (#${item.id})` : labels[i];
        walk(x, y, [...path, label], true);
      });
      // Same entries, same values: only the order moved.
      if (rows.length === start) {
        rows.push({ label: [...path, "Order"].join(" › "), before: b.map(itemLabel), after: a.map(itemLabel) });
      }
      return;
    }
    rows.push({ label: path.join(" › "), before: b, after: a, ...(entry && { entry }) });
  };
  walk(before, after, [fieldLabel(field)]);
  return rows;
};

/**
 * The back-office users list (/rbac/users) returns ids as "user_42", while
 * audit rows and GET /audit-logs use the numeric users.id (42).
 */
export const toAuditUserId = (id) => String(id ?? "").replace(/^user_/, "");

const splitList = (v) =>
  (v || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * URLSearchParams -> applied filters. Anything missing or invalid falls back
 * to the defaults (today, page 1, 25 rows), so a hand-edited URL can't break
 * the page.
 */
export const filtersFromSearchParams = (sp, now = new Date()) => {
  const get = (k) => (sp && sp.get(k)) || "";
  const preset = PRESETS.some((p) => p.key === get("preset")) ? get("preset") : "";

  let from = get("from") ? new Date(get("from")) : null;
  let to = get("to") ? new Date(get("to")) : null;
  let effectivePreset = preset;
  if (preset && preset !== "custom") {
    ({ from, to } = presetRange(preset, now));
  } else if (!from || isNaN(from) || !to || isNaN(to)) {
    ({ from, to } = presetRange("today", now));
    effectivePreset = "today";
  } else {
    effectivePreset = "custom";
  }

  const limit = Number(get("limit"));
  const page = Number(get("page"));
  const status = STATUSES.includes(get("status")) ? get("status") : "";

  return {
    preset: effectivePreset,
    from,
    to,
    // Older links carry "user_42"; the filter works on the numeric id.
    userIds: [...new Set(splitList(get("users")).map(toAuditUserId))],
    roles: splitList(get("roles")),
    actions: splitList(get("actions")),
    modules: splitList(get("modules")),
    status,
    q: get("q").trim(),
    page: Number.isInteger(page) && page > 0 ? page : 1,
    limit: PAGE_SIZES.includes(limit) ? limit : DEFAULT_PAGE_SIZE,
  };
};

/**
 * Filters -> URL params object for useUpdateSearchParams ("" deletes a key).
 * Presets other than "custom" store only the preset, so "Today" keeps meaning
 * today after a reload tomorrow.
 */
export const filtersToSearchParams = (f) => {
  const isCustom = f.preset === "custom";
  return {
    preset: f.preset && f.preset !== "today" ? f.preset : "",
    from: isCustom && f.from ? f.from.toISOString() : "",
    to: isCustom && f.to ? f.to.toISOString() : "",
    users: (f.userIds || []).join(","),
    roles: (f.roles || []).join(","),
    actions: (f.actions || []).join(","),
    modules: (f.modules || []).join(","),
    status: f.status || "",
    q: (f.q || "").trim(),
    page: f.page && f.page > 1 ? String(f.page) : "",
    limit: f.limit && f.limit !== DEFAULT_PAGE_SIZE ? String(f.limit) : "",
  };
};

/**
 * Filters -> API query string for GET /audit-logs (and /export). Arrays use
 * the `key[]=a&key[]=b` form the backend expects. Empty filters are omitted.
 * `withPaging: false` drops page/limit (used by export).
 */
export const filtersToQueryString = (f, { withPaging = true } = {}) => {
  const qs = new URLSearchParams();
  if (f.from) qs.append("from", f.from.toISOString());
  if (f.to) qs.append("to", f.to.toISOString());
  const arrays = {
    userIds: f.userIds,
    roles: f.roles,
    actions: f.actions,
    modules: f.modules,
  };
  Object.entries(arrays).forEach(([key, list]) => {
    (list || []).forEach((v) => qs.append(`${key}[]`, v));
  });
  if (f.status) qs.append("status", f.status);
  if (f.q && f.q.trim()) qs.append("q", f.q.trim());
  if (withPaging) {
    qs.append("page", String(f.page || 1));
    qs.append("limit", String(f.limit || DEFAULT_PAGE_SIZE));
  }
  return qs.toString();
};

/** "Showing 1–25 of 130 entries" (or "Showing 0 of 0 entries"). */
export const showingText = (page, limit, total) => {
  if (!total) return "Showing 0 of 0 entries";
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return `Showing ${start}–${end} of ${total} entries`;
};

/** Fallback one-line summary when the backend didn't send one. */
export const summarize = (item) => {
  if (!item) return "";
  if (item.summary) return item.summary;
  const what = [item.entityType, item.entityName || item.entityId].filter(Boolean).join(" ");
  return [humanize(item.action), what].filter(Boolean).join(" ");
};

/** Badge colour group for an action. */
export const actionTone = (action) => {
  if (["create", "approve", "login"].includes(action)) return "green";
  if (["delete", "reject", "login_failed"].includes(action)) return "red";
  if (["publish", "unpublish"].includes(action)) return "purple";
  if (["upload", "import", "export"].includes(action)) return "amber";
  if (["update", "settings_change", "role_change"].includes(action)) return "blue";
  return "grey";
};
